(() => {
    "use strict";

    const STORAGE_KEY = "spRentalCartPublicId";
    const SELECTION_STORAGE_KEY = "spRentalSelection";
    const INVOICE_STORAGE_KEY = "spRentalInvoiceUrl";
    const BUFFER_DAY_HINT = "Online nicht mehr buchbar. Telefonisch ist eine Buchung eventuell noch möglich.";
    let timingRulesPromise = null;

    // ===============================
    // Zentrale Datumsregeln laden
    // ===============================
    function loadTimingRules(apiBaseUrl) {
        if (
            window.SpVerleihTimingRules?.get_earliestAllowedStartDate
            && window.SpVerleihTimingRules?.get_pickupAndReturnDate_from_selectedPeriod
        ) {
            return Promise.resolve(window.SpVerleihTimingRules);
        }
        if (timingRulesPromise) return timingRulesPromise;

        const baseUrl = String(apiBaseUrl || window.location.origin).replace(/\/$/, "");
        timingRulesPromise = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = `${baseUrl}/spVerleih/timing-rules.js`;
            script.async = true;
            script.dataset.spRentalTimingRules = "true";
            script.onload = () => {
                if (
                    !window.SpVerleihTimingRules?.get_earliestAllowedStartDate
                    || !window.SpVerleihTimingRules?.get_pickupAndReturnDate_from_selectedPeriod
                ) {
                    timingRulesPromise = null;
                    reject(new Error("Die Regeln für die Datumsauswahl konnten nicht initialisiert werden."));
                    return;
                }
                resolve(window.SpVerleihTimingRules);
            };
            script.onerror = () => {
                script.remove();
                timingRulesPromise = null;
                reject(new Error("Die Regeln für die Datumsauswahl konnten nicht geladen werden."));
            };
            document.head.append(script);
        });

        return timingRulesPromise;
    }

    class SpRental {
        constructor(root) {
            this.root = root;
            this.apiBaseUrl = String(root.dataset.apiBaseUrl || "").replace(/\/$/, "");
            this.rentalType = root.dataset.rentalType === "saisonverleih" ? "saisonverleih" : "verleih";
            this.isSeasonalRental = this.rentalType === "saisonverleih";
            const storageSuffix = this.isSeasonalRental ? ":saisonverleih" : "";
            this.storageKey = `${STORAGE_KEY}${storageSuffix}`;
            this.selectionStorageKey = `${SELECTION_STORAGE_KEY}${storageSuffix}`;
            this.invoiceStorageKey = `${INVOICE_STORAGE_KEY}${storageSuffix}`;
            this.state = {
                options: null,
                seasons: [],
                selectedSeason: null,
                cart: null,
                products: [],
                rentalDays: null,
                pendingBooking: null,
                selectedPersonId: null,
                selectedLocation: null,
                rangeStart: null,
                rangeEnd: null,
                timingResult: null,
                calendarMonth: null,
                activeProductFilter: "all",
                searchReady: false,
                searchContext: null,
                currentStep: "selection",
            };
            this.root.classList.add("is-selection-step");

            this.bindEvents();
            window.addEventListener("pageshow", event => {
                if (event.persisted) this.refreshCartAfterReturn();
            });
            this.initializeProductsSubmitAnchor();
            this.initializeCalendar();
            this.initialize();
        }

        // ===============================
        // Frontend starten
        // Katalogoptionen und ein vorhandener Cart werden aus dem Backend geladen.
        // ===============================
        async initialize() {
            try {
                this.setLoading(true, this.isSeasonalRental ? "Saisonverleih wird vorbereitet …" : "Verleih wird vorbereitet …");
                console.log(`${this.isSeasonalRental ? "Saisonverleih" : "Verleih"} wird initialisiert`);
                await this.loadOptions();
                if (this.isSeasonalRental) await this.loadSeasons();

                const publicId = sessionStorage.getItem(this.storageKey);
                let selectedLocation = null;
                if (publicId) {
                    try {
                        const result = await this.api(this.cartApiPath(publicId));
                        if (result.cart?.type !== this.rentalType) {
                            throw new Error("Der gespeicherte Warenkorb gehört zu einer anderen Verleihart.");
                        }
                        if (result.cart?.status === "draft") {
                            sessionStorage.removeItem(this.invoiceStorageKey);
                            this.setCart(result.cart);
                            selectedLocation = result.cart.location || null;
                        } else if (result.cart?.status === "checkout") {
                            this.setCart(result.cart);
                            selectedLocation = result.cart.location || null;
                            this.showCheckoutRecovery();
                        } else {
                            sessionStorage.removeItem(this.storageKey);
                            sessionStorage.removeItem(this.selectionStorageKey);
                            sessionStorage.removeItem(this.invoiceStorageKey);
                            this.showNotice(`Die vorherige Buchung kann nicht mehr bearbeitet werden. Du kannst einen neuen ${this.isSeasonalRental ? "Saisonverleih" : "Verleih"} starten.`, "info");
                        }
                    } catch (error) {
                        sessionStorage.removeItem(this.storageKey);
                        sessionStorage.removeItem(this.selectionStorageKey);
                        sessionStorage.removeItem(this.invoiceStorageKey);
                        this.showNotice("Der vorherige Cart konnte nicht geladen werden. Bitte starte erneut.", "info");
                    }
                }
                await this.restoreSelection(selectedLocation, this.state.cart?.seasonId || null);
            } catch (error) {
                this.showNotice(error.message, "error");
            } finally {
                this.setLoading(false);
            }
        }

        // ===============================
        // Klicks und Formulare verbinden
        // Alle Aktionen der Section werden an einer zentralen Stelle verarbeitet.
        // ===============================
        bindEvents() {
            this.root.addEventListener("click", event => this.handleClick(event));
            this.root.addEventListener("change", event => {
                if (event.target.matches('input[name="bookingPerson"]')) this.updateConfirmBookingButton();
                if (event.target.matches("[data-multi-location-acknowledgement], [data-terms-acknowledgement]")) this.updateCheckoutButton();
                if (event.target.matches("[data-variant-select]")) this.updateVariantSelection(event.target);
            });
            this.root.addEventListener("keydown", event => {
                if (event.key !== "Escape") return;
                if (!this.root.querySelector("[data-product-image-modal]").hidden) this.closeProductImage();
                else if (!this.root.querySelector("[data-edit-person-modal]").hidden) this.closeEditPerson();
                else if (!this.root.querySelector("[data-person-panel]").hidden) this.cancelPendingBooking();
            });
            this.root.querySelector("[data-person-form]").addEventListener("submit", event => this.createPerson(event, true));
            this.root.querySelector("[data-edit-person-form]").addEventListener("submit", event => this.updatePerson(event));
        }

        // ===============================
        // Mobilen Produkt-Button andocken
        // Der fixe Button springt an seine normale Position, sobald diese sichtbar wird.
        // ===============================
        initializeProductsSubmitAnchor() {
            const anchor = this.root.querySelector("[data-products-submit-anchor]");
            this.productsSubmitObserver = new IntersectionObserver(entries => {
                const entry = entries[0];
                const anchorReached = entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight;
                this.root.classList.toggle("is-products-submit-at-anchor", anchorReached);
            });
            this.productsSubmitObserver.observe(anchor);
        }

        async handleClick(event) {
            const stepTarget = event.target.closest("[data-show-step], [data-step-button]");
            if (stepTarget) {
                const step = stepTarget.dataset.showStep || stepTarget.dataset.stepButton;
                this.showStep(step);
                return;
            }

            const target = event.target.closest("[data-action]");
            if (!target) return;

            const action = target.dataset.action;
            try {
                if (action === "previous-month") this.changeCalendarMonth(-1);
                if (action === "next-month") this.changeCalendarMonth(1);
                if (action === "select-location") this.selectLocation(target.dataset.location);
                if (action === "select-season") await this.selectSeason(target.dataset.seasonId);
                if (action === "select-calendar-day") await this.selectCalendarDay(target.dataset.date);
                if (action === "filter-products") this.setProductFilter(target.dataset.mainCategory);
                if (action === "toggle-product-points") this.toggleProductPoints(target);
                if (action === "open-product-image") this.openProductImage(target);
                if (action === "close-product-image") this.closeProductImage();
                if (action === "load-products") await this.loadProducts();
                if (action === "select-variant") await this.selectVariant(target.dataset.productId, target.dataset.variantId);
                if (action === "select-chosen-variant") await this.selectVariant(target.dataset.productId, target.dataset.variantId);
                if (action === "confirm-booking") await this.confirmBooking();
                if (action === "cancel-booking") this.cancelPendingBooking();
                if (action === "show-person-form") this.showPersonForm();
                if (action === "cancel-person-form") this.hideAndResetForm("[data-person-form]");
                if (action === "edit-person") this.openEditPerson(target.dataset.personId);
                if (action === "cancel-edit-person") this.closeEditPerson();
                if (action === "remove-booking") await this.removeBooking(target.dataset.bookingId);
                if (action === "remove-person") await this.removePerson(target.dataset.personId, target.closest("[data-person-panel]"));
                if (action === "checkout") await this.checkout();
                if (action === "continue-checkout") this.continueCheckout();
                if (action === "reopen-checkout") await this.reopenCheckout();
            } catch (error) {
                const personModal = target.closest("[data-person-panel]");
                const checkoutRecoveryModal = target.closest("[data-checkout-recovery-modal]");
                if (checkoutRecoveryModal) this.showCheckoutRecoveryNotice(error.message);
                else if (personModal) this.showPersonModalNotice(personModal, error.message, "error");
                else this.showNotice(error.message, "error");
            }
        }

        // ===============================
        // Backend ansprechen
        // Fehlerantworten werden in normale JavaScript-Fehler umgewandelt.
        // ===============================
        async api(path, options = {}) {
            const response = await fetch(`${this.apiBaseUrl}${path}`, {
                method: options.method || "GET",
                headers: {
                    "Accept": "application/json",
                    ...(options.body ? { "Content-Type": "application/json" } : {}),
                },
                body: options.body ? JSON.stringify(options.body) : undefined,
                credentials: "omit",
            });

            let data;
            try {
                data = await response.json();
            } catch (error) {
                throw new Error("Das Backend hat keine gültige Antwort geliefert.");
            }

            if (!response.ok || !data?.ok) {
                const apiError = new Error(data?.message || "Die Anfrage ist fehlgeschlagen.");
                apiError.status = response.status;
                throw apiError;
            }
            return data;
        }

        // ===============================
        // URL für Cart-Anfragen erstellen
        // Verleihart und öffentliche Cart-ID werden für alle Cart-Routes gemeinsam aufgebaut.
        // ===============================
        cartApiPath(publicId = this.state.cart?.publicId, suffix = "") {
            if (!publicId) throw new Error("Cart wurde nicht gefunden.");
            return `/sp-verleih/carts/${encodeURIComponent(this.rentalType)}/${encodeURIComponent(publicId)}${suffix}`;
        }

        // ===============================
        // Katalogoptionen laden
        // Filialen und Hauptkategorien werden in die Auswahlfelder eingetragen.
        // ===============================
        async loadOptions() {
            console.log("loadOptions", this.rentalType)
            const result = await this.api(`/sp-verleih/catalog/options?type=${encodeURIComponent(this.rentalType)}`);
            console.log("loadOptions res", result);
            this.state.options = result;

            this.renderLocations(result.locations || []);
            this.renderProductFilters();
        }

        // ===============================
        // Verfügbare Saisons laden
        // ===============================
        async requestSeasons() {
            // return [{ name: "Winter 2026/27", start: "01.10.2026", end: "31.03.2027" }];
            const res = {ok: false, message: "", seasons: null}
            try {
                const result = await this.api(`/sp-verleih/get-seasons`);
                console.log("requestSeasons res", result);
                if (!result?.ok) throw new Error(result?.message || "Unbekannter Server Fehler");

                res.ok = true;
                res.message = result.message;
                res.seasons = result.seasons;

                return res;

            } catch (error) {
                console.error(`Fehler bei requestSeasons()`, error);
                res.message = error.message;
                return res;
            };
        }

        async loadSeasons() {
            const getSeasons = await this.requestSeasons();
            if (!getSeasons?.ok) console.error("Saisons konnten nicht geladen werden", getSeasons?.message);

            let seasons = getSeasons?.ok && Array.isArray(getSeasons?.seasons) 
                ? getSeasons.seasons
                : []
            ;

            seasons = seasons.map(season => ({
                id: Number(season.id),
                name: String(season.name || "").trim(),
                start: this.germanDateToIso(season.startDate),
                end: this.germanDateToIso(season.endDate),
            }))            
            seasons = seasons.filter(season => Number.isInteger(season.id) && season.id > 0 && season.name && season.start && season.end && season.end >= this.getLocalToday());
            this.state.seasons = seasons;
            this.renderSeasons();
        }

        renderSeasons() {
            const container = this.root.querySelector("[data-season-options]");
            const selection = this.root.querySelector("[data-season-selection]");
            if (!container) return;
            if (!this.state.seasons.length) {
                container.innerHTML = '<p class="sp-rental__empty">Aktuell ist keine Saison verfügbar.</p>';
                if (selection) selection.hidden = true;
                return;
            }
            container.innerHTML = this.state.seasons.map(season => `
                <button
                    type="button"
                    class="${String(this.state.selectedSeason?.id) === String(season.id) ? "is-active" : ""}"
                    data-action="select-season"
                    data-season-id="${this.escape(String(season.id))}"
                    aria-pressed="${String(String(this.state.selectedSeason?.id) === String(season.id))}"
                >
                    <strong>${this.escape(season.name)}</strong>
                    <small>${this.date(season.start)} – ${this.date(season.end)}</small>
                </button>
            `).join("");
            if (selection) {
                selection.hidden = !this.state.selectedSeason;
                selection.innerHTML = this.state.selectedSeason ? `
                    <div><span>Abholung ab</span><strong>${this.dateWithWeekday(this.state.rangeStart)}</strong></div>
                    <div><span>Rückgabe bis</span><strong>${this.dateWithWeekday(this.state.rangeEnd)}</strong></div>
                ` : "";
            }
        }

        renderLocations(locations) {
            const container = this.root.querySelector("[data-location-options]");
            container.innerHTML = locations.map(location => `
                <div class="sp-rental__location-option">
                    <button type="button" data-action="select-location" data-location="${this.escape(location)}" aria-pressed="false">
                        ${this.escape(this.humanize(location))}
                    </button>
                    <small class="sp-rental__active-cart-location" data-current-cart data-location="${this.escape(location)}" hidden></small>
                </div>
            `).join("");
        }

        renderProductFilters() {
            const container = this.root.querySelector("[data-main-category-filters]");
            const categories = this.state.products.length
                ? [...new Set(this.state.products.map(product => product.mainCategory).filter(Boolean))].sort()
                : [];
            container.innerHTML = `
                <button type="button" class="is-active" data-action="filter-products" data-main-category="all">Alle</button>
                ${categories.map(category => `
                    <button type="button" data-action="filter-products" data-main-category="${this.escape(category)}">${this.escape(this.humanize(category))}</button>
                `).join("")}
            `;
        }

        // ===============================
        // Passenden Cart bereitstellen
        // Für die gewählte Filiale wird bei Bedarf ein Cart der passenden Verleihart erstellt.
        // ===============================
        async ensureCart(location) {
            if (this.state.cart?.status === "draft" && this.state.cart.location === location) {
                if (this.isSeasonalRental) {
                    const seasonId = this.state.selectedSeason?.id;
                    if (!seasonId) throw new Error("Bitte wähle zuerst eine Saison aus.");
                    if (String(this.state.cart.seasonId) !== String(seasonId)) {
                        const result = await this.api(this.cartApiPath(undefined, "/season"), {
                            method: "PATCH",
                            body: { seasonId },
                        });
                        this.setCart(result.cart);
                    }
                }
                return;
            }

            if (this.state.cart?.bookings?.length) {
                const confirmed = window.confirm("Beim Wechsel der Filiale wird ein neuer Warenkorb begonnen und die bestehende Auswahl geht verloren. Fortfahren?");
                if (!confirmed) throw new Error("Die bisherige Filiale wurde beibehalten.");
            }

            this.setLoading(true, "Warenkorb wird erstellt …");
            try {
                const result = await this.api("/sp-verleih/carts", {
                    method: "POST",
                    body: {
                        location,
                        type: this.rentalType,
                        ...(this.isSeasonalRental ? { seasonId: this.state.selectedSeason?.id } : {}),
                    },
                });
                sessionStorage.setItem(this.storageKey, result.cart.publicId);
                this.setCart(result.cart);
            } finally {
                this.setLoading(false);
            }
        }

        // ===============================
        // Verfügbare Produkte laden
        // Auswahl, Verleihart und Zeitraum werden vom Backend geprüft und mit aktuellen Preisen beantwortet.
        // ===============================
        async loadProducts() {
            const location = this.state.selectedLocation;
            let startDate = this.state.rangeStart;
            let endDate = this.state.rangeEnd;
            if (!location || !startDate || !endDate) {
                throw new Error(this.isSeasonalRental
                    ? "Bitte wähle Standort und Saison vollständig aus."
                    : "Bitte wähle Standort und Zeitraum vollständig aus.");
            }

            // Zeitraum direkt vor der Produktsuche erneut zentral prüfen
            if (!this.isSeasonalRental) {
                const timingResult = await this.validateRentalPeriod(startDate, endDate);
                this.applyTimingResult(timingResult);
                startDate = this.state.rangeStart;
                endDate = this.state.rangeEnd;
                this.renderCalendar();
                this.saveSelection();
            }

            await this.ensureCart(location);
            this.setLoading(true, "Verfügbarkeit wird geprüft …");
            console.log("loadProducts");
            try {
                const result = await this.api("/sp-verleih/catalog/products", {
                    method: "POST",
                    body: {
                        location,
                        startDate,
                        endDate,
                        type: this.rentalType,
                        ...(this.isSeasonalRental ? { seasonId: this.state.selectedSeason?.id } : {}),
                    },
                });
                console.log("loadProducts res", result);
                if (this.isSeasonalRental && result.startDate && result.endDate) {
                    this.state.rangeStart = result.startDate;
                    this.state.rangeEnd = result.endDate;
                    this.saveSelection();
                }
                this.state.products = result.products || [];
                this.state.rentalDays = result.rentalDays;
                this.state.activeProductFilter = "all";
                this.state.searchReady = true;
                this.state.searchContext = {
                    location,
                    startDate: this.state.rangeStart,
                    endDate: this.state.rangeEnd,
                    season: this.state.selectedSeason?.name || null,
                };
                this.renderProductFilters();
                this.renderProducts();
                this.showStep("products");
            } finally {
                this.setLoading(false);
            }
        }

        renderProducts() {
            const container = this.root.querySelector("[data-products]");
            const empty = this.root.querySelector("[data-products-empty]");
            const panel = this.root.querySelector("[data-products-panel]");
            panel.hidden = false;
            empty.hidden = this.state.products.length > 0;
            this.root.querySelector("[data-rental-days]").textContent = this.isSeasonalRental
                // ? (this.state.selectedSeason?.name || "")
                ? ""
                : (this.state.rentalDays ? `${this.state.rentalDays} Miettag${this.state.rentalDays === 1 ? "" : "e"}` : "");
            const context = this.state.searchContext;
            this.root.querySelector("[data-product-search-summary]").textContent = context
                ? `${context.location}${context.season ? ` · ${context.season}` : ""}`
                : ""
            ;
            this.root.querySelector("[data-product-search-summary-date]").textContent = context
                ? `${this.date(context.startDate)} – ${this.date(context.endDate)}`
                : ""
            ;

            const visibleProducts = this.state.activeProductFilter === "all"
                ? this.state.products
                : this.state.products.filter(product => product.mainCategory === this.state.activeProductFilter);
            empty.hidden = visibleProducts.length > 0;
            container.innerHTML = visibleProducts.map(product => {
                const imageUrl = this.safeImageUrl(product.imageUrl);
                const bulletPoints = this.getBulletPoints(product.bulletPoints);
                const variants = product.variants || [];
                const firstVariant = variants[0];
                const hasMultipleVariants = variants.length > 1;
                return `
                <article class="sp-rental__product">
                    <div class="sp-rental__product-overview">
                        <div class="sp-rental__product-media">
                            ${imageUrl
                                ? `<button type="button" class="sp-rental__product-image ${this.rentalType === "saisonverleih" ? `cursorZoomIn" data-action="open-product-image"` : `"`} aria-label="${this.escape(product.name)} vergrößert anzeigen" aria-haspopup="dialog">
                                    <img src="${this.escape(imageUrl)}" alt="${this.escape(product.name)}" loading="lazy" width="180" height="180">
                                    ${this.rentalType === "saisonverleih" && `<span class="sp-rental__product-image-expand" aria-hidden="true">⛶</span>`}
                                </button>`
                                : '<span>Bild folgt</span>'}
                        </div>
                        <div class="sp-rental__product-copy">
                            <h3>${this.escape(product.name)}</h3>
                            ${bulletPoints.length ? `
                                <div class="sp-rental__product-points-wrap" data-product-points-wrap>
                                    <div class="sp-rental__product-points-clip" data-product-points-clip>
                                        <ul class="sp-rental__product-points">
                                            ${bulletPoints.map(point => `<li>${this.escape(point)}</li>`).join("")}
                                        </ul>
                                    </div>
                                    <button type="button" class="sp-rental__product-points-toggle" data-action="toggle-product-points" aria-expanded="false" hidden>
                                        <span data-product-points-toggle-label>Mehr anzeigen</span>
                                        <span class="sp-rental__product-points-chevron" aria-hidden="true">⌄</span>
                                    </button>
                                </div>
                            ` : ""}
                        </div>
                    </div>
                    <div class="sp-rental__variant-picker">
                        ${hasMultipleVariants ? `
                            <label class="sp-rental__variant-select" for="variant-select-${this.escape(String(product.id))}">
                                <span>Variante auswählen</span>
                                <span class="sp-rental__variant-select-control">
                                    <select id="variant-select-${this.escape(String(product.id))}" data-variant-select data-product-id="${this.escape(String(product.id))}">
                                        ${variants.map(variant => `
                                            <option value="${this.escape(String(variant.id))}">${this.escape(variant.title)}</option>
                                        `).join("")}
                                    </select>
                                    <span class="sp-rental__variant-select-chevron" aria-hidden="true">▾</span>
                                </span>
                            </label>
                        ` : `
                            <div class="sp-rental__single-variant">
                                <span>Variante</span>
                                <strong>${this.escape(firstVariant?.title || "Standard")}</strong>
                            </div>
                        `}
                        <div class="sp-rental__variant-picker-price" data-variant-price>
                            <strong>${this.money(product?.totalAmount)}</strong>
                            <small>${this.isSeasonalRental ? "Saisonpreis" : `${this.money(product?.pricePerDay)} / Tag`}</small>
                        </div>
                        <button
                            type="button"
                            class="sp-rental__button sp-rental__button--small"
                            data-action="select-chosen-variant"
                            data-product-id="${this.escape(String(product.id))}"
                            data-variant-id="${this.escape(String(firstVariant?.id))}"
                        >Auswählen</button>
                    </div>
                </article>
            `;
            }).join("");
            container.querySelectorAll(".sp-rental__product-media img").forEach(image => {
                image.addEventListener("error", () => {
                    image.closest(".sp-rental__product-media").textContent = "Bild nicht verfügbar";
                }, { once: true });
            });
            window.requestAnimationFrame(() => this.initializeProductPointToggles(container));
        }

        // ===============================
        // Produktbild groß anzeigen
        // Das gewählte Bild wird in einem zentralen Dialog geöffnet und der Hintergrund gesperrt.
        // ===============================
        openProductImage(button) {
            const sourceImage = button.querySelector("img");
            const modal = this.root.querySelector("[data-product-image-modal]");
            const largeImage = modal.querySelector("[data-product-image-large]");
            if (!sourceImage || !largeImage) return;

            this.imageModalPreviousFocus = document.activeElement;
            this.imageModalPreviousBodyOverflow = document.body.style.overflow;
            largeImage.src = sourceImage.currentSrc || sourceImage.src;
            largeImage.alt = sourceImage.alt;
            modal.hidden = false;
            document.body.style.overflow = "hidden";
            modal.querySelector(".sp-rental__image-modal-dialog").focus();
        }

        // ===============================
        // Große Bildansicht schließen
        // Bild, Scroll-Zustand und Tastaturfokus werden wieder auf den vorherigen Zustand gesetzt.
        // ===============================
        closeProductImage() {
            const modal = this.root.querySelector("[data-product-image-modal]");
            if (!modal || modal.hidden) return;

            modal.hidden = true;
            const largeImage = modal.querySelector("[data-product-image-large]");
            largeImage.removeAttribute("src");
            largeImage.alt = "";
            document.body.style.overflow = this.imageModalPreviousBodyOverflow || "";
            if (this.imageModalPreviousFocus?.isConnected) this.imageModalPreviousFocus.focus();
            this.imageModalPreviousFocus = null;
        }

        // ===============================
        // Lange Produktbeschreibungen erkennen
        // Der Aufklapp-Button wird nur eingeblendet, wenn die begrenzte Fläche wirklich überläuft.
        // ===============================
        initializeProductPointToggles(container) {
            container.querySelectorAll("[data-product-points-wrap]").forEach(wrapper => {
                const clip = wrapper.querySelector("[data-product-points-clip]");
                const button = wrapper.querySelector("[data-action='toggle-product-points']");
                if (!clip || !button) return;

                const isOverflowing = clip.scrollHeight > clip.clientHeight + 1;
                wrapper.classList.toggle("is-collapsible", isOverflowing);
                button.hidden = !isOverflowing;
            });
        }

        // ===============================
        // Produktbeschreibung auf- oder zuklappen
        // Der Text und der Pfeil zeigen jederzeit, welcher Zustand als Nächstes möglich ist.
        // ===============================
        toggleProductPoints(button) {
            const wrapper = button.closest("[data-product-points-wrap]");
            if (!wrapper) return;

            const isExpanded = wrapper.classList.toggle("is-expanded");
            button.setAttribute("aria-expanded", String(isExpanded));
            const label = button.querySelector("[data-product-points-toggle-label]");
            if (label) label.textContent = isExpanded ? "Weniger anzeigen" : "Mehr anzeigen";
        }

        // ===============================
        // Gewählte Variante im Produkt aktualisieren
        // Preis und Auswählen-Button folgen unmittelbar der Dropdown-Auswahl.
        // ===============================
        updateVariantSelection(select) {
            const product = this.state.products.find(item => String(item.id) === String(select.dataset.productId));
            const variant = product?.variants?.find(item => String(item.id) === String(select.value));
            const picker = select.closest(".sp-rental__variant-picker");
            if (!variant || !picker) return;

            const price = picker.querySelector("[data-variant-price]");
            price.innerHTML = `<strong>${this.money(product.totalAmount)}</strong><small>${this.isSeasonalRental ? "Saisonpreis" : `${this.money(product.pricePerDay)} / Tag`}</small>`;
            picker.querySelector("[data-action='select-chosen-variant']").dataset.variantId = String(variant.id);
        }

        setProductFilter(mainCategory) {
            this.state.activeProductFilter = mainCategory || "all";
            this.root.querySelectorAll("[data-action='filter-products']").forEach(button => {
                button.classList.toggle("is-active", button.dataset.mainCategory === this.state.activeProductFilter);
            });
            this.renderProducts();
        }

        // ===============================
        // Variante auswählen
        // Bei Ski-Produkten wird vor dem Hinzufügen eine passende Person verlangt.
        // ===============================
        async selectVariant(productId, variantId) {
            const product = this.state.products.find(item => String(item.id) === String(productId));
            const variant = product?.variants?.find(item => String(item.id) === String(variantId));
            if (!product || !variant) throw new Error("Das ausgewählte Produkt wurde nicht gefunden.");

            this.state.pendingBooking = {
                product,
                variant,
                startDate: this.state.rangeStart,
                endDate: this.state.rangeEnd,
            };
            this.state.selectedPersonId = null;

            if (!product.requiresPersonAssignment && !product.requiresPersonData) {
                await this.addBooking(null);
                return;
            }

            this.preModalFocus = document.activeElement;
            this.previousBodyOverflow = document.body.style.overflow;
            this.root.querySelector("[data-person-panel]").hidden = false;
            this.root.querySelector("[data-person-panel] [data-person-modal-notice]").hidden = true;
            document.body.style.overflow = "hidden";
            this.root.querySelector("[data-person-product-text]").textContent = `${product.name} – ${variant.title}`;
            this.renderPersonChoice();
            this.root.querySelector(".sp-rental__modal-dialog").focus();
        }

        renderPersonChoice() {
            const persons = this.state.cart?.persons || [];
            const container = this.root.querySelector("[data-person-choice]");
            if (!persons.length) {
                container.innerHTML = "<p>Noch keine Person angelegt.</p>";
                this.updateConfirmBookingButton();
                return;
            }
            container.innerHTML = persons.map(person => `
                <div class="sp-rental__person-option">
                    <label class="sp-rental__person-option-select">
                        <input type="radio" name="bookingPerson" value="${this.escape(String(person.id))}" ${String(person.id) === String(this.state.selectedPersonId) ? "checked" : ""}>
                        <span><strong>${this.escape(person.vorname)}</strong><small>${person.heightCm ? `${this.escape(String(person.heightCm))} cm` : ""}${person.weightKg ? ` · ${this.escape(String(person.weightKg))} kg` : ""}</small></span>
                    </label>
                    <button type="button" class="sp-rental__person-delete" data-action="remove-person" data-person-id="${this.escape(String(person.id))}" aria-label="${this.escape(person.vorname)} löschen">Löschen</button>
                </div>
            `).join("");
            this.updateConfirmBookingButton();
        }

        updateConfirmBookingButton() {
            const button = this.root.querySelector("[data-action='confirm-booking']");
            const modal = this.root.querySelector("[data-person-panel]");
            button.disabled = modal.hidden || !this.root.querySelector('input[name="bookingPerson"]:checked');
        }

        showPersonForm() {
            this.state.selectedPersonId = null;
            this.root.querySelectorAll('input[name="bookingPerson"]').forEach(input => {
                input.checked = false;
            });
            this.updateConfirmBookingButton();

            const form = this.root.querySelector("[data-person-form]");
            form.hidden = false;
            form.querySelector("input, select")?.focus();
        }

        async confirmBooking() {
            const selected = this.root.querySelector('input[name="bookingPerson"]:checked');
            if (!selected) throw new Error("Bitte wähle eine Person aus oder lege eine neue Person an.");
            await this.addBooking(selected.value);
        }

        async addBooking(personId) {
            const pending = this.state.pendingBooking;
            if (!pending) throw new Error("Es wurde keine Variante ausgewählt.");

            this.setLoading(true, "Artikel wird hinzugefügt …");
            try {
                const result = await this.api(this.cartApiPath(undefined, "/bookings"), {
                    method: "POST",
                    body: {
                        variantId: pending.variant.id,
                        personId: personId || null,
                        startDate: pending.startDate,
                        endDate: pending.endDate,
                    },
                });
                this.setCart(result.cart);
                this.cancelPendingBooking();
                this.showNotice("Der Artikel wurde hinzugefügt.", "success");
            } finally {
                this.setLoading(false);
            }
        }

        cancelPendingBooking() {
            this.state.pendingBooking = null;
            this.state.selectedPersonId = null;
            this.root.querySelector("[data-person-panel]").hidden = true;
            document.body.style.overflow = this.previousBodyOverflow || "";
            this.hideAndResetForm("[data-person-form]");
            this.updateConfirmBookingButton();
            if (this.preModalFocus instanceof HTMLElement) this.preModalFocus.focus();
        }

        // ===============================
        // Person speichern
        // Das aktualisierte Cart-Objekt kommt vollständig vom Backend zurück.
        // ===============================
        async createPerson(event, useForPendingBooking) {
            event.preventDefault();
            if (!this.state.cart) throw new Error("Bitte starte zuerst den Verleih.");

            const form = event.currentTarget;
            const body = this.formData(form);
            for (const field of ["heightCm", "weightKg"]) {
                if (body[field] === "") body[field] = null;
                else if (body[field] !== undefined) body[field] = Number(body[field]);
            }
            for (const field of ["ageGroup", "skillLevel"]) {
                if (body[field] === "") body[field] = null;
            }

            this.setLoading(true, "Person wird gespeichert …");
            try {
                const previousIds = new Set((this.state.cart.persons || []).map(person => String(person.id)));
                const result = await this.api(this.cartApiPath(undefined, "/persons"), {
                    method: "POST",
                    body,
                });
                this.setCart(result.cart);
                const createdPerson = (result.cart.persons || []).find(person => !previousIds.has(String(person.id)));
                form.reset();
                form.querySelector("details")?.removeAttribute("open");
                form.hidden = true;

                if (useForPendingBooking && createdPerson) {
                    this.state.selectedPersonId = createdPerson.id;
                    this.renderPersonChoice();
                }
                this.showNotice("Person wurde gespeichert.", "success");
            } catch (error) {
                this.showNotice(error.message, "error");
            } finally {
                this.setLoading(false);
            }
        }

        // ===============================
        // Person bearbeiten
        // Die vorhandenen Daten werden in einem eigenen Fenster angezeigt und gespeichert.
        // ===============================
        openEditPerson(personId) {
            const person = (this.state.cart?.persons || []).find(item => String(item.id) === String(personId));
            if (!person) throw new Error("Die Person wurde nicht gefunden.");

            const modal = this.root.querySelector("[data-edit-person-modal]");
            const form = modal.querySelector("[data-edit-person-form]");
            this.editingPersonId = person.id;
            this.preEditPersonFocus = document.activeElement;
            this.previousEditBodyOverflow = document.body.style.overflow;

            for (const field of ["vorname", "heightCm", "weightKg", "ageGroup", "skillLevel"]) {
                form.elements[field].value = person[field] ?? "";
            }
            const requiresCompleteData = this.personRequiresCompleteData(person.id);
            for (const field of ["heightCm", "weightKg", "ageGroup", "skillLevel"]) {
                form.elements[field].required = requiresCompleteData;
            }
            const requirement = modal.querySelector("[data-edit-person-requirement]");
            requirement.hidden = !requiresCompleteData;

            modal.hidden = false;
            document.body.style.overflow = "hidden";
            modal.querySelector(".sp-rental__modal-dialog").focus();
        }

        closeEditPerson() {
            const modal = this.root.querySelector("[data-edit-person-modal]");
            if (modal.hidden) return;

            modal.hidden = true;
            modal.querySelector("[data-edit-person-form]").reset();
            this.editingPersonId = null;
            document.body.style.overflow = this.previousEditBodyOverflow || "";
            if (this.preEditPersonFocus instanceof HTMLElement) this.preEditPersonFocus.focus();
        }

        async updatePerson(event) {
            event.preventDefault();
            if (!this.state.cart || !this.editingPersonId) throw new Error("Die Person konnte nicht bearbeitet werden.");

            const body = this.formData(event.currentTarget);
            for (const field of ["heightCm", "weightKg"]) body[field] = Number(body[field]);
            for (const field of ["heightCm", "weightKg"]) {
                if (!Number.isFinite(body[field]) || body[field] <= 0) body[field] = null;
            }
            for (const field of ["ageGroup", "skillLevel"]) {
                if (body[field] === "") body[field] = null;
            }

            this.setLoading(true, "Person wird aktualisiert …");
            try {
                const result = await this.api(this.cartApiPath(undefined, `/persons/${encodeURIComponent(this.editingPersonId)}`), {
                    method: "PATCH",
                    body,
                });
                this.setCart(result.cart);
                this.closeEditPerson();
                this.showNotice("Person wurde aktualisiert.", "success");
            } catch (error) {
                this.showNotice(error.message, "error");
            } finally {
                this.setLoading(false);
            }
        }

        async removeBooking(bookingId) {
            if (!window.confirm("Diesen Artikel wirklich entfernen?")) return;
            this.setLoading(true, "Artikel wird entfernt …");
            try {
                const result = await this.api(this.cartApiPath(undefined, `/bookings/${encodeURIComponent(bookingId)}`), { method: "DELETE" });
                this.setCart(result.cart);
                this.showNotice("Der Artikel wurde entfernt.", "success");
            } finally {
                this.setLoading(false);
            }
        }

        async removePerson(personId, modal) {
            if (!personId || !modal) throw new Error("Die Person konnte nicht eindeutig zugeordnet werden.");
            if (!window.confirm("Diese Person wirklich entfernen?")) return;
            this.setLoading(true, "Person wird entfernt …");
            try {
                const result = await this.api(this.cartApiPath(undefined, `/persons/${encodeURIComponent(personId)}`), { method: "DELETE" });
                if (String(this.state.selectedPersonId) === String(personId)) this.state.selectedPersonId = null;
                this.setCart(result.cart);
                this.showPersonModalNotice(modal, "Die Person wurde entfernt.", "success");
            } catch (error) {
                const message = error.status === 409
                    ? "Diese Person kann nicht gelöscht werden, solange ihr noch ein Artikel im Warenkorb zugeordnet ist. Entferne zuerst die zugeordneten Artikel."
                    : error.message;
                this.showPersonModalNotice(modal, message, "error");
            } finally {
                this.setLoading(false);
            }
        }

        showPersonModalNotice(modal, message, type = "error") {
            const notice = modal?.querySelector("[data-person-modal-notice]");
            if (!notice) {
                this.showNotice(message, type);
                return;
            }
            notice.textContent = message;
            notice.dataset.type = type;
            notice.hidden = false;
            window.clearTimeout(this.personModalNoticeTimer);
            this.personModalNoticeTimer = window.setTimeout(() => { notice.hidden = true; }, 7000);
        }

        // ===============================
        // Checkout starten
        // Der Cart wird letztmalig geprüft und danach zur Zahlung in Shopify geöffnet.
        // ===============================
        async checkout() {
            if (!this.state.cart?.bookings?.length) {
                this.showNotice("Bitte füge zuerst mindestens einen Artikel hinzu.", "error");
                return;
            }
            const bookingsWithoutRequiredPerson = this.getBookingsWithoutRequiredPerson();
            if (bookingsWithoutRequiredPerson.length) {
                this.showNotice("Bitte entferne die betroffenen Artikel und füge sie erneut mit einer Person hinzu.", "error");
                return;
            }
            const incompletePersons = this.getIncompleteRequiredPersons();
            if (incompletePersons.length) {
                this.showNotice(`Bitte ergänze zuerst die Körperdaten für ${incompletePersons.map(person => person.vorname).join(", ")}.`, "error");
                return;
            }
            const termsAcknowledgement = this.root.querySelector("[data-terms-acknowledgement]");
            if (!termsAcknowledgement?.checked) {
                this.showNotice("Bitte akzeptiere zuerst die AGB (Mietbedingungen).", "error");
                return;
            }
            const acknowledgement = this.root.querySelector("[data-multi-location-acknowledgement]");
            if (this.getBookingLocationCount() > 1 && !acknowledgement?.checked) {
                this.showNotice("Bitte bestätige, dass die Artikel an mehreren Filialen abgeholt und zurückgegeben werden.", "error");
                return;
            }
            console.log("checkout", this.state.cart.publicId)
            this.setLoading(true, "Bitte warten…");
            try {
                const result = await this.api(this.cartApiPath(undefined, "/checkout"), {
                    method: "POST",
                });
                console.log("checkout result", result)
                sessionStorage.setItem(this.invoiceStorageKey, result.invoiceUrl);
                window.location.assign(result.invoiceUrl);
            } catch (error) {
                this.showNotice(error.message, "error");
            } finally {
                this.setLoading(false);
            }
        }

        // ===============================
        // Offenen Checkout nach Rückkehr anzeigen
        // Der Kunde kann zur Zahlung zurückkehren oder den Cart wieder öffnen.
        // ===============================
        showCheckoutRecovery() {
            const modal = this.root.querySelector("[data-checkout-recovery-modal]");
            const invoiceUrl = sessionStorage.getItem(this.invoiceStorageKey);
            modal.querySelector("[data-action='continue-checkout']").hidden = !invoiceUrl;
            modal.querySelector("[data-checkout-recovery-notice]").hidden = true;
            if (modal.hidden) this.checkoutRecoveryPreviousOverflow = document.body.style.overflow;
            modal.hidden = false;
            document.body.style.overflow = "hidden";
            modal.querySelector(".sp-rental__modal-dialog").focus();
        }

        closeCheckoutRecovery() {
            this.root.querySelector("[data-checkout-recovery-modal]").hidden = true;
            document.body.style.overflow = this.checkoutRecoveryPreviousOverflow || "";
            this.checkoutRecoveryPreviousOverflow = null;
        }

        showCheckoutRecoveryNotice(message) {
            const notice = this.root.querySelector("[data-checkout-recovery-notice]");
            notice.textContent = message;
            notice.dataset.type = "error";
            notice.hidden = false;
        }

        continueCheckout() {
            const invoiceUrl = sessionStorage.getItem(this.invoiceStorageKey);
            if (!invoiceUrl) throw new Error("Die Zahlungs-URL ist nicht mehr vorhanden. Bitte bearbeite den Warenkorb und starte die Zahlung erneut.");
            window.location.assign(invoiceUrl);
        }

        async reopenCheckout() {
            if (!this.state.cart?.publicId) throw new Error("Cart wurde nicht gefunden.");

            this.setLoading(true, "Checkout wird abgebrochen …");
            try {
                const result = await this.api(this.cartApiPath(undefined, "/reopen-checkout"), {
                    method: "POST",
                });
                sessionStorage.removeItem(this.invoiceStorageKey);
                this.setCart(result.cart);
                this.closeCheckoutRecovery();
                this.showStep(this.state.products.length ? "products" : "selection");
                this.showNotice("Der Warenkorb kann wieder bearbeitet werden.", "success");
            } catch (error) {
                this.showCheckoutRecoveryNotice(error.message);
            } finally {
                this.setLoading(false);
            }
        }

        async refreshCartAfterReturn() {
            const publicId = sessionStorage.getItem(this.storageKey);
            if (!publicId) return;

            try {
                const result = await this.api(this.cartApiPath(publicId));
                if (result.cart?.status === "checkout") {
                    this.setCart(result.cart);
                    this.showCheckoutRecovery();
                } else if (result.cart?.status === "draft") {
                    sessionStorage.removeItem(this.invoiceStorageKey);
                    this.setCart(result.cart);
                    this.closeCheckoutRecovery();
                } else {
                    sessionStorage.removeItem(this.storageKey);
                    sessionStorage.removeItem(this.selectionStorageKey);
                    sessionStorage.removeItem(this.invoiceStorageKey);
                }
            } catch (error) {
                this.showNotice(error.message, "error");
            }
        }

        // ===============================
        // Cart im Frontend anzeigen
        // Übersicht, Zähler und Kundendaten werden mit der Backend-Antwort aktualisiert.
        // ===============================
        setCart(cart) {
            if (!cart || cart.type !== this.rentalType) {
                throw new Error("Der Warenkorb gehört nicht zur ausgewählten Verleihart.");
            }
            this.state.cart = cart;
            sessionStorage.setItem(this.storageKey, cart.publicId);
            this.root.querySelectorAll("[data-current-cart]").forEach(indicator => {
                const isActiveCartRegion = indicator.dataset.location === cart.location;
                indicator.hidden = !isActiveCartRegion;
                indicator.textContent = isActiveCartRegion ? `Aktiver Warenkorb für ${this.humanize(cart.location)}` : "";
            });
            this.renderCart();
        }

        renderCart() {
            const cart = this.state.cart;
            const bookings = cart?.bookings || [];
            this.root.classList.toggle("has-cart-items", bookings.length > 0);
            const cartCount = this.root.querySelector("[data-cart-count]");
            cartCount.textContent = String(bookings.length);
            cartCount.hidden = bookings.length === 0;
            this.root.querySelector("[data-summary-count]").textContent = `${bookings.length} Artikel`;
            this.root.querySelector("[data-mobile-cart-count]").textContent = `${bookings.length} Artikel`;
            this.root.querySelector("[data-mobile-cart-total]").textContent = this.money(cart?.totalAmount || 0);

            const groupedCart = bookings.length
                ? this.buildGroupedCart(bookings, cart.totalAmount)
                : '<div class="sp-rental__empty">Noch keine Artikel ausgewählt.</div>';
            this.root.querySelector("[data-checkout-summary]").innerHTML = groupedCart;
            this.root.querySelector("[data-summary-content]").innerHTML = groupedCart;
            const multiLocationNotice = this.root.querySelector("[data-multi-location-notice]");
            const hasMultipleLocations = this.getBookingLocationCount(bookings) > 1;
            if (multiLocationNotice) multiLocationNotice.hidden = !hasMultipleLocations;
            const incompletePersons = this.getIncompleteRequiredPersons();
            const bookingsWithoutRequiredPerson = this.getBookingsWithoutRequiredPerson();
            const personDataNotice = this.root.querySelector("[data-person-data-notice]");
            if (personDataNotice) {
                personDataNotice.hidden = incompletePersons.length === 0 && bookingsWithoutRequiredPerson.length === 0;
                personDataNotice.textContent = bookingsWithoutRequiredPerson.length
                    ? "Bei mindestens einem Artikel fehlt die erforderliche Personenzuordnung. Bitte entferne den Artikel und füge ihn erneut hinzu."
                    : incompletePersons.length
                        ? `Bitte ergänze vor der Zahlung die Körperdaten für ${incompletePersons.map(person => person.vorname).join(", ")}.`
                        : "";
            }
            this.updateCheckoutButton();

            if (!this.root.querySelector("[data-person-panel]").hidden) this.renderPersonChoice();
        }

        // ===============================
        // Zahlungsbutton anhand der notwendigen Bestätigungen steuern
        // Der Button wird erst nach Annahme der AGB und gegebenenfalls der Filialbestätigung aktiv.
        // ===============================
        updateCheckoutButton() {
            const hasMultipleLocations = this.getBookingLocationCount() > 1;
            const acknowledgement = this.root.querySelector("[data-multi-location-acknowledgement]");
            const termsAcknowledgement = this.root.querySelector("[data-terms-acknowledgement]");
            const checkoutButton = this.root.querySelector("[data-action='checkout']");
            const hasIncompletePersonData = this.getIncompleteRequiredPersons().length > 0;
            const hasMissingPersonAssignment = this.getBookingsWithoutRequiredPerson().length > 0;
            if (!hasMultipleLocations && acknowledgement) acknowledgement.checked = false;
            if (checkoutButton) checkoutButton.disabled = hasIncompletePersonData
                || hasMissingPersonAssignment
                || !termsAcknowledgement?.checked
                || (hasMultipleLocations && !acknowledgement?.checked);
        }

        // ===============================
        // Konkrete Filialen direkt aus den Bookings ermitteln
        // Jedes Booking enthält seine konkrete Filiale direkt.
        // ===============================
        getBookingLocation(booking) {
            return booking?.location || null;
        }

        getBookingLocationKey(booking) {
            const location = this.getBookingLocation(booking);
            if (location?.id != null) return `location-${location.id}`;
            if (booking?.locationId != null) return `location-${booking.locationId}`;
            if (location?.code) return `location-code-${location.code}`;
            if (location?.name) return `location-name-${location.name}`;
            return "unassigned-location";
        }

        getBookingLocationCount(bookings = this.state.cart?.bookings || []) {
            return new Set(bookings.map(booking => this.getBookingLocationKey(booking))).size;
        }

        // ===============================
        // Warenkorb nach Filialen und Personen gruppieren
        // Zuerst wird die konkrete Abholstation gezeigt, darunter folgen Personen und Artikel.
        // ===============================
        buildGroupedCart(bookings, totalAmount) {
            const locationGroups = new Map();
            for (const booking of bookings) {
                const location = this.getBookingLocation(booking);
                const locationKey = this.getBookingLocationKey(booking);
                if (!locationGroups.has(locationKey)) locationGroups.set(locationKey, { location, persons: new Map() });
                const personKey = booking.person?.id ? `person-${booking.person.id}` : "unassigned";
                const persons = locationGroups.get(locationKey).persons;
                if (!persons.has(personKey)) persons.set(personKey, { person: booking.person || null, bookings: [] });
                persons.get(personKey).bookings.push(booking);
            }

            const content = Array.from(locationGroups.values()).map(locationGroup => {
                const location = locationGroup.location;
                const locationName = location?.name || this.state.cart?.location || "Noch nicht zugeordnet";
                const address = location
                    ? [location.strasse, [location.plz, location.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ")
                    : this.state.cart?.location || "Abholstation";
                const personContent = Array.from(locationGroup.persons.values()).map(group => {
                const person = group.person;
                const personDetails = person ? this.formatPersonDetails(person) : "";
                const requiresCompleteData = person && group.bookings.some(booking => booking.variant?.product?.requiresPersonData);
                const personDataIncomplete = requiresCompleteData && !this.hasCompletePersonData(person);
                return `
                    <section class="sp-rental__cart-group">
                        ${person ? `
                            <header class="sp-rental__cart-group-heading">
                                <div>
                                    <h3><span>${this.escape(person.vorname)}</span></h3>
                                    ${personDetails ? `<small>${this.escape(personDetails)}</small>` : ""}
                                    ${personDataIncomplete ? `
                                        <button
                                            type="button"
                                            class="sp-rental__person-data-warning"
                                            data-action="edit-person"
                                            data-person-id="${this.escape(String(person.id))}"
                                        >Körperdaten vor der Zahlung ergänzen</button>
                                    ` : ""}
                                </div>
                                <button type="button" class="sp-rental__edit-person" data-action="edit-person" data-person-id="${this.escape(String(person.id))}">Bearbeiten</button>
                            </header>
                        ` : ""}
                        <div class="sp-rental__cart-group-items">
                            ${group.bookings.map(booking => `
                                <article class="sp-rental__grouped-item">
                                    <div class="sp-rental__grouped-item-main">
                                        <strong>${this.escape(booking.variant?.product?.name || "Produkt")} – ${this.escape(booking.variant?.title || "Variante")}</strong>
                                        <small>${this.date(booking.startDate)} – ${this.date(booking.endDate)}</small>
                                    </div>
                                    <div class="sp-rental__grouped-item-end">
                                        <strong>${this.money(booking.totalAmount)}</strong>
                                        <button type="button" class="sp-rental__link-danger" data-action="remove-booking" data-booking-id="${this.escape(String(booking.id))}">Entfernen</button>
                                    </div>
                                </article>
                            `).join("")}
                        </div>
                    </section>
                `;
                }).join("");
                return `
                    <section class="sp-rental__location-group">
                        <div class="sp-rental__location-heading">
                            <span>Abholstation:</span>
                            <strong>${this.escape(locationName)}</strong>
                            <small class="sp-rental__location-address">${this.escape(address)}</small>
                        </div>
                        ${personContent}
                    </section>
                `;
            }).join("");

            return `${content}<div class="sp-rental__total"><span>Gesamt</span><strong>${this.money(totalAmount)}</strong></div>`;
        }

        formatPersonDetails(person) {
            const ageGroups = {
                u10: "Unter 10 Jahre",
                "10-49": "10 - 49 Jahre",
                "50-59": "50 - 59 Jahre",
                "60-69": "60 - 69 Jahre",
                "70+": "70 Jahre und älter",
                child: "Kind",
                youth: "Jugendlich",
                adult: "Erwachsen",
                senior: "Senior",
            };
            const skillLevels = {
                "1 Anfänger": "1 Anfänger",
                "2 Allrounder": "2 Allrounder",
                "3 Fortgeschritten": "3 Fortgeschritten",
                "3+ Top Sportlich": "3+ Top Sportlich",
                beginner: "Anfänger",
                intermediate: "Fortgeschritten",
                advanced: "Sehr erfahren",
            };
            return [
                person.heightCm ? `${person.heightCm} cm` : "",
                person.weightKg ? `${person.weightKg} kg` : "",
                ageGroups[person.ageGroup] || person.ageGroup || "",
                skillLevels[person.skillLevel] || person.skillLevel || "",
            ].filter(Boolean).join(" · ");
        }

        // ===============================
        // Vollständige Körperdaten einer Person prüfen
        // Der Name allein reicht für die Zuordnung; Ski benötigen zusätzliche Angaben.
        // ===============================
        hasCompletePersonData(person) {
            return Boolean(
                String(person?.vorname || "").trim()
                && Number(person?.heightCm) > 0
                && Number(person?.weightKg) > 0
                && person?.ageGroup
                && person?.skillLevel
            );
        }

        personRequiresCompleteData(personId) {
            return (this.state.cart?.bookings || []).some(booking =>
                String(booking.personId) === String(personId)
                && Boolean(booking.variant?.product?.requiresPersonData)
            );
        }

        // ===============================
        // Fehlende Personenzuordnungen erkennen
        // Damit werden auch ältere oder inkonsistente Warenkörbe vor dem Checkout abgefangen.
        // ===============================
        getBookingsWithoutRequiredPerson() {
            return (this.state.cart?.bookings || []).filter(booking =>
                !booking.person
                && Boolean(
                    booking.variant?.product?.requiresPersonAssignment
                    || booking.variant?.product?.requiresPersonData
                )
            );
        }

        getIncompleteRequiredPersons() {
            const persons = new Map();
            for (const booking of this.state.cart?.bookings || []) {
                if (!booking.variant?.product?.requiresPersonData || !booking.person) continue;
                if (!this.hasCompletePersonData(booking.person)) persons.set(String(booking.person.id), booking.person);
            }
            return [...persons.values()];
        }

        // ===============================
        // Ansichten und Statusmeldungen steuern
        // Nur der gewählte Hauptschritt ist sichtbar.
        // ===============================
        showStep(step) {
            if (!this.root.querySelector(`[data-step="${step}"]`)) return;
            if (step === "products" && !this.state.searchReady && !this.state.cart?.bookings?.length) {
                this.showNotice(this.isSeasonalRental
                    ? "Bitte lege zuerst Standort und Saison fest."
                    : "Bitte lege zuerst Standort und Zeitraum fest.", "info");
                step = "selection";
            }
            if (step === "checkout" && !this.state.cart?.bookings?.length) {
                this.showNotice("Bitte füge zuerst mindestens einen Artikel hinzu.", "info");
                step = this.state.searchReady ? "products" : "selection";
            }

            this.state.currentStep = step;
            for (const stepName of ["selection", "products", "checkout"]) {
                this.root.classList.toggle(`is-${stepName}-step`, step === stepName);
            }
            this.root.querySelectorAll("[data-step]").forEach(element => {
                element.hidden = element.dataset.step !== step;
            });
            this.root.querySelectorAll("[data-step-button]").forEach(button => {
                button.classList.toggle("is-active", button.dataset.stepButton === step);
            });
            this.root.scrollIntoView({ behavior: "smooth", block: "start" });
        }

        showNotice(message, type = "info") {
            const notice = this.root.querySelector("[data-notice]");
            notice.textContent = message;
            notice.dataset.type = type;
            notice.hidden = false;
            window.clearTimeout(this.noticeTimer);
            this.noticeTimer = window.setTimeout(() => { notice.hidden = true; }, 7000);
        }

        setLoading(active, text = "Wird geladen …") {
            const loading = this.root.querySelector("[data-loading]");
            loading.hidden = !active;
            loading.querySelector("[data-loading-text]").textContent = text;
            this.root.setAttribute("aria-busy", String(active));
        }

        // ===============================
        // Mietzeitraum zentral prüfen
        // Die gemeinsame Regeldatei bestätigt Zeitraum sowie Abhol- und Rückgabezeiten.
        // ===============================
        async validateRentalPeriod(startDate, endDate) {
            const validatePeriod = window.SpVerleihTimingRules?.get_pickupAndReturnDate_from_selectedPeriod;
            if (!validatePeriod) throw new Error("Die Regeln für die Datumsauswahl sind nicht verfügbar.");

            const result = await validatePeriod(startDate, endDate, true);
            if (!result?.ok) throw new Error(result?.message || "Der gewählte Mietzeitraum ist nicht gültig.");
            return result;
        }

        // ===============================
        // Bestätigten Zeitraum übernehmen
        // Automatische Anpassungen der Regeln werden direkt in Kalender und State gespeichert.
        // ===============================
        applyTimingResult(result) {
            this.state.rangeStart = result.confirmedStartDate;
            this.state.rangeEnd = result.confirmedEndDate;
            this.state.timingResult = result;
        }

        // ===============================
        // Kalender vorbereiten
        // Der Kalender startet im aktuellen Monat der Zeitzone Europe/Berlin.
        // ===============================
        initializeCalendar() {
            const [year, month] = this.getLocalToday().split("-").map(Number);
            this.state.calendarMonth = new Date(Date.UTC(year, month - 1, 1));
            this.renderCalendar();
        }

        selectLocation(location) {
            this.root.querySelectorAll("[data-action='select-location']").forEach(button => {
                const selected = button.dataset.location === location;
                button.classList.toggle("is-active", selected);
                button.setAttribute("aria-pressed", String(selected));
            });
            if (!location) {
                this.state.selectedLocation = null;
                this.state.searchReady = false;
                this.root.querySelector("[data-calendar-panel]").hidden = true;
                this.root.querySelector("[data-season-panel]").hidden = true;
                this.updateProductsSubmitButton();
                this.saveSelection();
                return;
            }
            if (this.state.selectedLocation && this.state.selectedLocation !== location) {
                this.state.rangeStart = null;
                this.state.rangeEnd = null;
                this.state.timingResult = null;
                this.state.selectedSeason = null;
            }
            this.state.selectedLocation = location;
            this.state.searchReady = false;
            const selectionPanel = this.root.querySelector(this.isSeasonalRental ? "[data-season-panel]" : "[data-calendar-panel]");
            this.root.querySelector("[data-calendar-panel]").hidden = this.isSeasonalRental;
            this.root.querySelector("[data-season-panel]").hidden = !this.isSeasonalRental;
            if (this.isSeasonalRental) this.renderSeasons();
            else this.renderCalendar();
            this.updateProductsSubmitButton();
            this.saveSelection();
            selectionPanel.scrollIntoView({ behavior: "smooth", block: "start" });
        }

        // ===============================
        // Saison auswählen
        // Mietbeginn ist Saisonstart oder heute; das Mietende bleibt immer das Saisonende.
        // ===============================
        async selectSeason(seasonId) {
            const season = this.state.seasons.find(item => String(item.id) === String(seasonId));
            if (!season) throw new Error("Die ausgewählte Saison ist nicht verfügbar.");
            if (String(this.state.selectedSeason?.id) === String(season.id)) return;
            const canChangePeriod = await this.clearBookingsBeforePeriodChange();
            if (!canChangePeriod) return;

            this.applySeason(season);
            this.state.searchReady = false;
            this.renderSeasons();
            this.updateProductsSubmitButton();
            this.saveSelection();
        }

        applySeason(season) {
            const today = this.getLocalToday();
            this.state.selectedSeason = season;
            this.state.rangeStart = season.start < today ? today : season.start;
            this.state.rangeEnd = season.end;
            this.state.timingResult = null;
        }

        changeCalendarMonth(change) {
            const currentMonth = this.state.calendarMonth;
            const targetMonth = new Date(Date.UTC(currentMonth.getUTCFullYear(), currentMonth.getUTCMonth() + change, 1));
            const [todayYear, todayMonth] = this.getLocalToday().split("-").map(Number);
            const firstAllowedMonth = Date.UTC(todayYear, todayMonth - 1, 1);
            if (targetMonth.getTime() < firstAllowedMonth) return;
            this.state.calendarMonth = targetMonth;
            this.renderCalendar();
        }

        renderCalendar() {
            if (!this.state.calendarMonth) return;
            const month = this.state.calendarMonth;
            const year = month.getUTCFullYear();
            const monthIndex = month.getUTCMonth();
            const today = this.getLocalToday();
            const earliestAllowedStartDate = this.isSeasonalRental
                ? today
                : window.SpVerleihTimingRules.get_earliestAllowedStartDate();
            const firstWeekday = (new Date(Date.UTC(year, monthIndex, 1)).getUTCDay() + 6) % 7;
            const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
            const cells = Array.from({ length: firstWeekday }, () => '<span class="sp-rental__calendar-empty"></span>');
            const timingResult = this.state.timingResult;
            const pickupExtensionDate = timingResult
                ? (timingResult.earliestPickUpDate !== timingResult.pickUpDate ? timingResult.earliestPickUpDate : null)
                : (this.state.rangeStart ? this.addDays(this.state.rangeStart, -1) : null);
            const firstReturnExtensionDate = this.state.rangeEnd ? this.addDays(this.state.rangeEnd, 1) : null;
            const returnExtensionDate = timingResult?.latestReturnDate || (firstReturnExtensionDate && this.isSunday(firstReturnExtensionDate)
                ? this.addDays(firstReturnExtensionDate, 1)
                : firstReturnExtensionDate);

            for (let day = 1; day <= daysInMonth; day++) {
                const date = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                const disabled = date < earliestAllowedStartDate;
                const isBufferDay = !this.isSeasonalRental && date >= today && disabled;
                const isPickupExtension = date === pickupExtensionDate;
                const isStart = date === this.state.rangeStart;
                const isEnd = date === this.state.rangeEnd;
                const inRange = this.state.rangeStart && this.state.rangeEnd && date > this.state.rangeStart && date < this.state.rangeEnd;
                const isReturnExtension = date === returnExtensionDate;
                const isReturnExtensionBridge = firstReturnExtensionDate && returnExtensionDate
                    && date >= firstReturnExtensionDate && date < returnExtensionDate;
                const classes = [
                    isPickupExtension ? "is-pickup-extension" : "",
                    isStart ? "is-start" : "",
                    isEnd ? "is-end" : "",
                    inRange ? "is-in-range" : "",
                    isReturnExtensionBridge ? "is-return-extension-bridge" : "",
                    isReturnExtension ? "is-return-extension" : "",
                ].filter(Boolean).join(" ");
                const dayButton = `<button type="button" class="${classes}" data-action="select-calendar-day" data-date="${date}" ${disabled ? "disabled" : ""} aria-label="${this.date(date)}${isPickupExtension ? ", Abholung für den folgenden Miettag ab 16 Uhr möglich" : ""}${isStart ? ", erster Miettag" : ""}${isEnd ? ", letzter Miettag" : ""}${isReturnExtensionBridge ? ", keine Rückgabe möglich; Rückgabezeitraum läuft bis Montag weiter" : ""}${isReturnExtension ? ", Rückgabe bis 11 Uhr möglich" : ""}">${day}</button>`;
                cells.push(isBufferDay
                    ? `<span class="sp-rental__calendar-buffer-day" tabindex="0" role="note" aria-label="${this.date(date)}: ${BUFFER_DAY_HINT}" data-tooltip="${BUFFER_DAY_HINT}">${dayButton}</span>`
                    : dayButton);
            }

            this.root.querySelector("[data-calendar-title]").textContent = new Intl.DateTimeFormat("de-DE", {
                month: "long",
                year: "numeric",
                timeZone: "UTC",
            }).format(month);
            this.root.querySelector("[data-calendar-days]").innerHTML = cells.join("");

            const [todayYear, todayMonth] = today.split("-").map(Number);
            const previousButton = this.root.querySelector("[data-action='previous-month']");
            previousButton.disabled = Date.UTC(year, monthIndex, 1) <= Date.UTC(todayYear, todayMonth - 1, 1);
            this.renderCalendarSelection();
        }

        async selectCalendarDay(date) {
            if (!date || date < this.getLocalToday()) return;

            if (!this.state.rangeStart || this.state.rangeEnd) {
                const startValidation = await this.validateRentalPeriod(date, date);
                const canChangePeriod = await this.clearBookingsBeforePeriodChange();
                if (!canChangePeriod) return;
                this.state.rangeStart = startValidation.confirmedStartDate;
                this.state.rangeEnd = null;
                this.state.timingResult = null;
                if (startValidation.confirmedStartDate !== date) {
                    this.showNotice("Der Sonntag ist als erster Miettag nicht möglich. Der Mietbeginn wurde auf Samstag angepasst.", "info");
                }
            } else if (date < this.state.rangeStart) {
                const startValidation = await this.validateRentalPeriod(date, date);
                const canChangePeriod = await this.clearBookingsBeforePeriodChange();
                if (!canChangePeriod) return;
                this.state.rangeStart = startValidation.confirmedStartDate;
                this.state.timingResult = null;
                if (startValidation.confirmedStartDate !== date) {
                    this.showNotice("Der Sonntag ist als erster Miettag nicht möglich. Der Mietbeginn wurde auf Samstag angepasst.", "info");
                }
            } else {
                const timingResult = await this.validateRentalPeriod(this.state.rangeStart, date);
                const canChangePeriod = await this.clearBookingsBeforePeriodChange();
                if (!canChangePeriod) return;
                this.applyTimingResult(timingResult);
                if (timingResult.timeFrameChanged) {
                    this.showNotice("Der gewählte Zeitraum wurde entsprechend unserer Abhol- und Rückgaberegeln angepasst.", "info");
                }
            }
            this.state.searchReady = false;
            this.renderCalendar();
            this.saveSelection();
        }

        // ===============================
        // Warenkorb vor einer Zeitraumänderung leeren
        // Nach Bestätigung werden alle Artikel gemeinsam entfernt, bevor das neue Datum gesetzt wird.
        // ===============================
        async clearBookingsBeforePeriodChange() {
            if (!this.state.cart?.bookings?.length) return true;
            const confirmed = window.confirm(this.isSeasonalRental
                ? "Wenn du die Saison änderst, werden alle bereits ausgewählten Artikel aus dem Warenkorb entfernt. Fortfahren?"
                : "Wenn du den Mietzeitraum änderst, werden alle bereits ausgewählten Artikel aus dem Warenkorb entfernt. Fortfahren?");
            if (!confirmed) return false;

            this.setLoading(true, "Warenkorb wird geleert …");
            try {
                const result = await this.api(this.cartApiPath(undefined, "/bookings"), {
                    method: "DELETE",
                });
                this.state.products = [];
                this.state.rentalDays = null;
                this.state.searchReady = false;
                this.state.searchContext = null;
                this.state.activeProductFilter = "all";
                this.root.querySelector("[data-products]").innerHTML = "";
                this.renderProductFilters();
                this.setCart(result.cart);
                this.showNotice(this.isSeasonalRental
                    ? "Der Warenkorb wurde für die neue Saison geleert."
                    : "Der Warenkorb wurde für den neuen Mietzeitraum geleert.", "info");
                return true;
            } catch (error) {
                this.showNotice(error.message, "error");
                return false;
            } finally {
                this.setLoading(false);
            }
        }

        renderCalendarSelection() {
            const output = this.root.querySelector("[data-calendar-selection]");
            const start = this.state.rangeStart ? this.date(this.state.rangeStart) : "–";
            const end = this.state.rangeEnd ? this.date(this.state.rangeEnd) : "–";
            const rentalDays = this.state.rangeStart && this.state.rangeEnd
                ? Math.floor((Date.parse(`${this.state.rangeEnd}T00:00:00Z`) - Date.parse(`${this.state.rangeStart}T00:00:00Z`)) / 86400000) + 1
                : null;
            const timingResult = this.state.timingResult;
            const formatTime = value => String(value || "").replace(/^0/, "").replace(/:00$/, "");
            const uniqueOptions = options => options.filter((option, index, all) =>
                option.date && option.time
                && all.findIndex(item => item.date === option.date && item.time === option.time) === index
            );
            const pickupOptions = timingResult ? uniqueOptions([
                { date: timingResult.earliestPickUpDate, time: timingResult.earliestPickUpTime },
                { date: timingResult.pickUpDate, time: timingResult.pickUpTime },
            ]) : [];
            const returnOptions = timingResult ? uniqueOptions([
                { date: timingResult.returnDate, time: timingResult.returnTime },
                { date: timingResult.latestReturnDate, time: timingResult.latestReturnTime },
            ]) : [];
            const renderHandoverOptions = (options, prefix) => options.map((option, index) => `
                ${index ? "<em>oder</em>" : ""}
                <div><strong>${this.dateWithWeekday(option.date)}</strong> <small>${prefix} ${formatTime(option.time)} Uhr</small></div>
            `).join("");
            output.innerHTML = `
                <div class="sp-rental__date-status sp-rental__date-status--duration ${rentalDays ? "" : "is-missing"}">
                    <span>Mietdauer</span>
                    <strong>${rentalDays ? `${rentalDays} Miettag${rentalDays === 1 ? "" : "e"}` : "Noch nicht festgelegt"}</strong>
                    <small>${rentalDays ? `${start} – ${end}` : "Bitte ersten und letzten Miettag wählen"}</small>
                </div>
                <div class="sp-rental__date-status sp-rental__date-status--handover ${pickupOptions.length ? "" : "is-missing"}">
                    <span>Abholung</span>
                    ${pickupOptions.length
                        ? `<div class="sp-rental__handover-options">${renderHandoverOptions(pickupOptions, "ab")}</div>`
                        : '<strong>Noch offen</strong><small>Nach vollständiger Auswahl des Mietzeitraums</small>'}
                </div>
                <div class="sp-rental__date-status sp-rental__date-status--handover ${returnOptions.length ? "" : "is-missing"}">
                    <span>Rückgabe</span>
                    ${returnOptions.length
                        ? `<div class="sp-rental__handover-options">${renderHandoverOptions(returnOptions, "bis")}</div>`
                        : '<strong>Noch offen</strong><small>Nach vollständiger Auswahl des Mietzeitraums</small>'}
                </div>
            `;
            this.updateProductsSubmitButton();
        }

        updateProductsSubmitButton() {
            const button = this.root.querySelector("[data-products-submit]");
            const validSelection = Boolean(
                this.state.selectedLocation
                && this.state.rangeStart
                && this.state.rangeEnd
                && (this.isSeasonalRental || this.state.timingResult)
                && (!this.isSeasonalRental || this.state.selectedSeason)
            );
            button.disabled = !validSelection;
            this.root.classList.toggle("has-valid-selection", validSelection);
        }

        // ===============================
        // Auswahl im aktuellen Tab merken
        // Standort und Zeitraum beziehungsweise Saison bleiben auch nach einem Reload erhalten.
        // ===============================
        saveSelection() {
            sessionStorage.setItem(this.selectionStorageKey, JSON.stringify({
                type: this.rentalType,
                location: this.state.selectedLocation,
                startDate: this.state.rangeStart,
                endDate: this.state.rangeEnd,
                seasonId: this.state.selectedSeason?.id || null,
                season: this.state.selectedSeason?.name || null,
            }));
        }

        async restoreSelection(fallbackLocation = null, fallbackSeasonId = null) {
            let saved = {};
            try {
                saved = JSON.parse(sessionStorage.getItem(this.selectionStorageKey) || "{}");
            } catch (error) {
                sessionStorage.removeItem(this.selectionStorageKey);
            }

            const availableLocations = this.state.options?.locations || [];
            const location = availableLocations.includes(saved.location)
                ? saved.location
                : (availableLocations.includes(fallbackLocation) ? fallbackLocation : null);
            if (this.isSeasonalRental) {
                const seasonId = fallbackSeasonId || saved.seasonId;
                const season = this.state.seasons.find(item =>
                    seasonId
                        ? String(item.id) === String(seasonId)
                        : item.name === saved.season
                );
                if (season) this.applySeason(season);
                else {
                    this.state.selectedSeason = null;
                    this.state.rangeStart = null;
                    this.state.rangeEnd = null;
                    this.state.timingResult = null;
                }
            } else {
                const validStart = /^\d{4}-\d{2}-\d{2}$/.test(saved.startDate || "");
                const validEnd = /^\d{4}-\d{2}-\d{2}$/.test(saved.endDate || "")
                    && validStart
                    && saved.endDate >= saved.startDate;
                if (validStart && validEnd) {
                    try {
                        const timingResult = await this.validateRentalPeriod(saved.startDate, saved.endDate);
                        this.applyTimingResult(timingResult);
                    } catch (error) {
                        this.state.rangeStart = null;
                        this.state.rangeEnd = null;
                        this.state.timingResult = null;
                        sessionStorage.removeItem(this.selectionStorageKey);
                    }
                } else {
                    this.state.rangeStart = null;
                    this.state.rangeEnd = null;
                    this.state.timingResult = null;
                }
            }
            if (location) this.selectLocation(location);
            else {
                this.renderCalendarSelection();
                this.updateProductsSubmitButton();
            }
        }

        getLocalToday() {
            const parts = new Intl.DateTimeFormat("de-DE", {
                timeZone: "Europe/Berlin",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
            }).formatToParts(new Date());
            const date = Object.fromEntries(parts.map(part => [part.type, part.value]));
            return `${date.year}-${date.month}-${date.day}`;
        }

        isSunday(date) {
            return new Date(`${date}T00:00:00Z`).getUTCDay() === 0;
        }

        isSaturday(date) {
            return new Date(`${date}T00:00:00Z`).getUTCDay() === 6;
        }

        addDays(date, days) {
            const value = new Date(`${date}T00:00:00Z`);
            value.setUTCDate(value.getUTCDate() + days);
            return value.toISOString().slice(0, 10);
        }

        germanDateToIso(value) {
            const input = String(value ?? "").trim();
            let isoDate;

            const german = input.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
            const iso = input.match(
                /^(\d{4}-\d{2}-\d{2})(?:T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z)?$/
            );

            if (german) {
                isoDate = `${german[3]}-${german[2]}-${german[1]}`;
            } else if (iso) {
                if (Number.isNaN(Date.parse(input))) return null;
                isoDate = iso[1];
            } else {
                return null;
            }

            const date = new Date(`${isoDate}T00:00:00Z`);

            return Number.isNaN(date.getTime()) ||
                date.toISOString().slice(0, 10) !== isoDate
                ? null
                : isoDate;
        }


        formData(form) {
            return Object.fromEntries(new FormData(form).entries());
        }

        hideAndResetForm(selector) {
            const form = this.root.querySelector(selector);
            form.reset();
            form.querySelector("details")?.removeAttribute("open");
            form.hidden = true;
        }

        money(value) {
            return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(value || 0));
        }

        date(value) {
            return new Intl.DateTimeFormat("de-DE", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                timeZone: "UTC",
            }).format(new Date(value));
        }

        dateWithWeekday(value) {
            const weekday = new Intl.DateTimeFormat("de-DE", {
                weekday: "short",
                timeZone: "UTC",
            }).format(new Date(value)).replace(".", "");
            return `${weekday}, ${this.date(value)}`;
        }

        humanize(value) {
            return String(value || "").replace(/[-_]/g, " ").replace(/\b\w/g, letter => letter.toUpperCase());
        }

        getBulletPoints(value) {
            if (typeof value !== "string" || !value.trim()) return [];
            return value.split(";").map(point => point.trim()).filter(Boolean);
        }

        safeImageUrl(value) {
            if (!value) return "";
            try {
                const url = new URL(String(value), window.location.origin);
                return ["http:", "https:"].includes(url.protocol) ? url.href : "";
            } catch (error) {
                return "";
            }
        }

        escape(value) {
            return String(value ?? "")
                .replaceAll("&", "&amp;")
                .replaceAll("<", "&lt;")
                .replaceAll(">", "&gt;")
                .replaceAll('"', "&quot;")
                .replaceAll("'", "&#039;");
        }
    }

    // ===============================
    // Einfachen Testphasen-Schutz prüfen
    // Erst nach korrekter Eingabe wird das Verleihsystem initialisiert und die API aufgerufen.
    // ===============================
    async function initializeRentalRoot(root) {
        if (root.dataset.initialized || root.dataset.initializing) return;

        const protectionEnabled = root.dataset.testProtection === "true";
        const password = root.dataset.testPassword || "";
        const accessKey = `spRentalTestAccess:${root.id}:${password}`;
        const accessGranted = !protectionEnabled || sessionStorage.getItem(accessKey) === "granted";

        if (!accessGranted) {
            const form = root.querySelector("[data-test-access-form]");
            const error = root.querySelector("[data-test-access-error]");
            if (!form || form.dataset.bound) return;

            form.dataset.bound = "true";
            form.addEventListener("submit", async event => {
                event.preventDefault();
                const enteredPassword = String(new FormData(form).get("testPassword") || "");
                if (enteredPassword !== password || !password) {
                    error.hidden = false;
                    form.elements.testPassword.select();
                    return;
                }

                sessionStorage.setItem(accessKey, "granted");
                root.classList.remove("is-test-locked");
                root.querySelector("[data-test-access-gate]")?.remove();
                await initializeRentalRoot(root);
            });
            return;
        }

        root.classList.remove("is-test-locked");
        root.querySelector("[data-test-access-gate]")?.remove();
        root.dataset.initializing = "true";
        try {
            await loadTimingRules(root.dataset.apiBaseUrl);
            root.dataset.initialized = "true";
            new SpRental(root);
        } catch (error) {
            const notice = root.querySelector("[data-notice]");
            if (notice) {
                notice.textContent = error.message;
                notice.dataset.type = "error";
                notice.hidden = false;
            }
        } finally {
            delete root.dataset.initializing;
        }
    }

    function initializeRentalSections(container = document) {
        container.querySelectorAll("[data-sp-rental]").forEach(initializeRentalRoot);
    }

    document.addEventListener("DOMContentLoaded", () => initializeRentalSections());
    document.addEventListener("shopify:section:load", event => initializeRentalSections(event.target));
})();
